import React, { useEffect, useMemo, useRef } from 'react';
import { PieChartPro } from '@mui/x-charts-pro/PieChartPro';
import Box from '@mui/material/Box';
import type { GraphDistribution } from '../GraphBox.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import html2canvas from 'html2canvas';

interface PieChartProps {
	title: string;
	data: GraphDistribution[];
	registerExportFn?: (exportFn: () => void) => void;
	fullWidth?: boolean;
}

const CircadiaPieChart: React.FC<PieChartProps> = ({ title, data, registerExportFn, fullWidth = false }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

	const chartColorWheelValues: string[] = theme?.custom?.chartColorWheel || [];	

	const transformedData = useMemo(() => {
		if (fullWidth || !data || data.length <= 5) {
			return data;
		}

		// Sort data by value in descending order
		const sortedData = [...data].sort((a, b) => b.value - a.value);
		
		// Take top 5 items
		const topItems = sortedData.slice(0, 5);
		
		// Aggregate remaining items
		const otherItems = sortedData.slice(5);
		const otherValue = otherItems.reduce((sum, item) => sum + item.value, 0);
		
		// Create "Other" category
		const otherCategory: GraphDistribution = {
			id: -1, // Use -1 to ensure unique ID
			value: otherValue,
			label: "Other"
		};

		return [...topItems, otherCategory];
	}, [data, fullWidth]);

	const chartSize = useMemo(() => {
		if(fullWidth) return { width: 500, height: 500 };
		if (isMobile) return { width: 200, height: 200 };
		if (isMd) return { width: 225, height: 225 };
		if (isLgUp) return { width: 250, height: 250 };
		return { width: 250, height: 250 };
	}, [fullWidth, isMobile, isMd, isLgUp]);

	const chartRef = useRef<HTMLDivElement>(null);

	const handleExportChart = () => {
		if (chartRef.current) {
			html2canvas(chartRef.current, {
				allowTaint: true,
				useCORS: true,
				scale: 2, // Better quality
				backgroundColor: '#fff', // Set background to white
				logging: true,
			}).then((canvas) => {
				try {
					// Create a link to download the image
					const link = document.createElement('a');
					link.download = `${title.toLowerCase().replace(/\s+/g, '_')}.png`;
					link.href = canvas.toDataURL('image/png', 1.0);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} catch (error) {
					console.error('Error exporting chart:', error);
				}
			});
		}
	};

	// Register the export function with parent component when available
	useEffect(() => {
		if (registerExportFn) {
			registerExportFn(handleExportChart);
		}
	}, [registerExportFn]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<Box ref={chartRef} sx={{ position: 'relative' }}>
				<PieChartPro
					series={[
						{
							data: transformedData.map((item) => ({ ...item })),
						},
					]}
					slotProps={{
						legend: {
							direction: isMobile ? 'horizontal' : 'vertical',
						},
					}}
					colors={chartColorWheelValues}
					width={chartSize.width}
					height={chartSize.height}
					margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
					data-testid={`${title.toLowerCase().replace(/\s+/g, '-')}-pie-chart`}
				/>
			</Box>
		</Box>
	);
};

export default CircadiaPieChart;
